import { useQuery, useZero } from "@rocicorp/zero/react";
import { useMemo, useState } from "react";
import { sharedMutators } from "../../../../shared/mutators";
import { queries } from "../../../../shared/queries";
import type { ScopeKind } from "../../../../shared/rbac/permissions";
import { SCOPE_KINDS } from "../../../../shared/rbac/permissions";
import { useHasAccess } from "../../../hooks/useHasAccess";
import { useUrlSort } from "../../../hooks/useUrlSort";
import {
  useMakeUrl,
  navigate,
  Route,
  routes,
  useCurrentUrl,
} from "../../../lib/urls";
import { Button } from "../../Button";
import { ConfirmModal } from "../../ConfirmModal";
import { CountBadge } from "../../CountBadge";
import { Dropdown } from "../../Dropdown";
import { EmptyState } from "../../EmptyState";
import { Icon } from "../../Icon";
import { ListTable } from "../../ListTable";
import { Text } from "../../Text";
import { OverflowBadges } from "../OverflowBadges";
import {
  BuiltinLockBadge,
  Container,
  Header,
  HeaderCountBadge,
  HoverActions,
  NeutralIconBadge,
  PillBadge,
  RowNameLayout,
  RowNameLine,
  ScrollArea,
  SearchField,
} from "../shared";
import { CreateRoleModal } from "./CreateRoleModal";
import {
  PERMISSION_ICONS,
  permissionLabel,
  SCOPE_ICONS,
  SCOPE_LABELS,
} from "./constants";
import {
  buildRoleGrantCounts,
  buildRoleRows,
  countByScope,
  filterAndSortRoles,
  type SortKey,
} from "./lib";
import {
  RowActionPlaceholder,
  ScopeFilterTab,
  ScopeTabs,
  SubHeader,
} from "./styled";

export function RolesSettings() {
  const makeUrl = useMakeUrl();
  const z = useZero();
  const canManageRoles = useHasAccess("tenant:roles_manage");
  const [customRoles, customRolesResult] = useQuery(queries.allRolesLite());
  const [allUserGroups] = useQuery(queries.allUserGroups());
  const [allDirectUserGrantsRaw] = useQuery(queries.allDirectUserGrants());
  const [allDirectServiceAccountGrants] = useQuery(
    queries.allDirectServiceAccountGrants()
  );
  const [allSpaces] = useQuery(
    queries.spacesLite({ includeAdminSpaces: true })
  );
  const personalSpaceIds = useMemo(
    () =>
      new Set(allSpaces.filter((s) => s.mode === "personal").map((s) => s.id)),
    [allSpaces]
  );
  const allDirectUserGrants = useMemo(
    () =>
      allDirectUserGrantsRaw.filter((g) => !personalSpaceIds.has(g.scopeId)),
    [allDirectUserGrantsRaw, personalSpaceIds]
  );
  const [showDeleteModal, setShowDeleteModal] = useState<string | null>(null);

  const { q, scope: urlScope } = useCurrentUrl(routes.settings.roles);
  const search = q ?? "";
  const setSearch = (next: string) => {
    navigate(null, { q: next }, { replace: true });
  };
  const scopeFilter: ScopeKind = (SCOPE_KINDS as readonly string[]).includes(
    urlScope ?? ""
  )
    ? (urlScope as ScopeKind)
    : "space";

  const { sortColumn, setSort } = useUrlSort(routes.settings.roles, {
    name: "asc",
    createdAt: "desc",
  });
  const sort: SortKey = sortColumn ?? "name";

  const roleGrantCounts = useMemo(
    () =>
      buildRoleGrantCounts(
        allUserGroups,
        allDirectUserGrants,
        allDirectServiceAccountGrants
      ),
    [allUserGroups, allDirectUserGrants, allDirectServiceAccountGrants]
  );

  const allRoleRows = useMemo(
    () => buildRoleRows(customRoles, roleGrantCounts),
    [customRoles, roleGrantCounts]
  );

  const scopeCounts = useMemo(() => countByScope(allRoleRows), [allRoleRows]);

  const sortedRows = useMemo(
    () => filterAndSortRoles(allRoleRows, scopeFilter, search, sort),
    [allRoleRows, scopeFilter, search, sort]
  );

  const items = useMemo(() => {
    return sortedRows.map((row) => ({
      key: row.id,
      link: makeUrl(routes.modal.role, { id: row.id }),
      name: (
        <RowNameLayout>
          <NeutralIconBadge iconName={SCOPE_ICONS[row.scope]} />
          <RowNameLine>
            <Text
              ellipsis
              variant="text-regular-md"
              color="var(--text-primary)"
            >
              {row.name}
            </Text>
            {row.isBuiltin && <BuiltinLockBadge />}
          </RowNameLine>
        </RowNameLayout>
      ),
      permissions: (
        <OverflowBadges
          items={row.permissions}
          keyFn={(p) => p}
          renderBadge={(p) => (
            <PillBadge icon={PERMISSION_ICONS[p]}>
              {permissionLabel(p)}
            </PillBadge>
          )}
          overflowTooltip={(hidden) =>
            hidden.map((p) => permissionLabel(p)).join(", ")
          }
        />
      ),
      actions: (hasMouse: boolean) =>
        row.isBuiltin || !canManageRoles ? (
          <RowActionPlaceholder aria-hidden="true" />
        ) : (
          <HoverActions visible={hasMouse}>
            <Dropdown
              opener={<Button variant="secondary" icon="more" />}
              align="bottom"
              minWidth={160}
              sections={[
                {
                  items: [
                    {
                      type: "button" as const,
                      left: <Icon name="trash" size={16} />,
                      label: "Delete role",
                      disabled: row.grantsCount > 0,
                      onClick: () => {
                        setShowDeleteModal(row.id);
                      },
                    },
                  ],
                },
              ]}
            />
          </HoverActions>
        ),
    }));
  }, [sortedRows, canManageRoles, makeUrl]);

  const handleDelete = () => {
    if (!showDeleteModal) return;
    z.mutate(sharedMutators.deleteRole({ roleId: showDeleteModal }));
    setShowDeleteModal(null);
    navigate(routes.settings.roles);
  };

  const deleteTarget = customRoles.find((r) => r.id === showDeleteModal);

  return (
    <Container>
      <Header style={{ paddingRight: 12 }}>
        <Text variant="text-regular-md">Roles</Text>
        <HeaderCountBadge count={allRoleRows.length} />
        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <SearchField
            placeholder="Search roles…"
            value={search}
            onChange={setSearch}
          />
          {canManageRoles && (
            <Dropdown
              align="bottom"
              minWidth={180}
              opener={<Button size="l" icon="plus" />}
              sections={[
                {
                  label: "Create role for…",
                  items: SCOPE_KINDS.map((s) => ({
                    type: "link" as const,
                    left: <Icon name={SCOPE_ICONS[s]} size={16} />,
                    label: SCOPE_LABELS[s],
                    to: makeUrl(routes.settings.roles.new, { scopeKind: s }),
                  })),
                },
              ]}
            />
          )}
        </div>
      </Header>
      <SubHeader>
        <ScopeTabs>
          {SCOPE_KINDS.map((s) => (
            <ScopeFilterTab
              key={s}
              to={makeUrl(routes.settings.roles, { scope: s })}
              replace
              data-active={scopeFilter === s}
            >
              <Icon name={SCOPE_ICONS[s]} size={16} />
              <Text variant="text-small-md" color="inherit">
                {SCOPE_LABELS[s]}
              </Text>
              <CountBadge
                style={{
                  background: "var(--alpha4)",
                  height: 18,
                  padding: "0 6px",
                }}
              >
                <Text variant="text-smaller-md" color="var(--text-secondary)">
                  {scopeCounts[s]}
                </Text>
              </CountBadge>
            </ScopeFilterTab>
          ))}
        </ScopeTabs>
        <div style={{ marginLeft: "auto" }}>
          <Dropdown
            align="bottom"
            minWidth={180}
            opener={
              <Button
                variant="secondary"
                icon="arrow-down-up"
                aria-label="Sort"
              />
            }
            sections={[
              {
                items: [
                  {
                    type: "button" as const,
                    left: <Icon name="sparkles" size={16} />,
                    label: "Recently added",
                    right: (
                      <Icon
                        name="check"
                        size={12}
                        color={
                          sort === "createdAt"
                            ? "var(--text-secondary)"
                            : "transparent"
                        }
                      />
                    ),
                    onClick: () => setSort("createdAt"),
                  },
                  {
                    type: "button" as const,
                    left: <Icon name="arrow-down-a-z" size={16} />,
                    label: "Name",
                    right: (
                      <Icon
                        name="check"
                        size={12}
                        color={
                          sort === "name"
                            ? "var(--text-secondary)"
                            : "transparent"
                        }
                      />
                    ),
                    onClick: () => setSort("name"),
                  },
                ],
              },
            ]}
          />
        </div>
      </SubHeader>
      <ScrollArea>
        <ListTable
          headers={{
            name: "Name",
            permissions: "Permissions",
            actions: "",
          }}
          columns={{ permissions: "flex" }}
          hideHeaders
          items={items}
          emptyState={
            customRolesResult.type === "complete" ? (
              <EmptyState
                iconName="id-card-lanyard"
                title="No roles"
                description="Try a different search."
              />
            ) : null
          }
        />
      </ScrollArea>

      <Route path={routes.settings.roles.new}>
        {({ scopeKind: sk }) => (
          <CreateRoleModal
            open
            onClose={() => navigate(routes.settings.roles)}
            scopeKind={
              (SCOPE_KINDS as readonly string[]).includes(sk ?? "")
                ? (sk as ScopeKind)
                : "space"
            }
          />
        )}
      </Route>

      <ConfirmModal
        open={showDeleteModal !== null}
        onClose={() => setShowDeleteModal(null)}
        onConfirm={handleDelete}
        title="Delete custom role"
        description={
          deleteTarget
            ? `“${deleteTarget.name}” will be permanently deleted.`
            : ""
        }
        confirmLabel="Delete"
        destructive
      />
    </Container>
  );
}
